// React
import React, { useContext, useEffect, useState } from 'react';

// Context
import { AppContext } from '../../../model/context/app/app';

// Config
import { leadsListConfig as config } from './leadsList.config';

// Libs
import { Grid, Pagination, Segment } from 'semantic-ui-react';

// Components
import LeadsListToolbarComponent from './LeadsListToolbar.component';
import LeadsTabQuickFilterComponent from '../../../components/lead/list/filter/LeadsTabQuickFilter.component';
import LeadsTableComponent from '../../../components/lead/list/LeadsTable.component';

// Services
import ACL_importAllLeads from '../../../services/ACL/ACL_importLeads';
import fetchLeads from '../../../services/fetch/fetchLeads';

// Types
import { LeadsCallQueryParamsType, LeadsForViewType, LeadTabId, paginationState } from '../../../types/LeadsList';
import { LeadStateType } from '../../../types/States';
import { FetchResponseType } from '../../../types/Fetch';
import { WorkflowStatesContext } from '../../../model/context/app/workflowStates';
import { useSearchParams } from 'react-router-dom';

import { useTranslation } from 'react-i18next';
import { LoaderState } from '../../../types/LoaderState';
import SemanticDropdownWrapperComponent from '../../../components/lead/list/filter/SemanticDropdownWrapperComponent';
import LeadsSortingComponent from '../../../components/lead/list/filter/LeadsSorting.component';
import { LeadSource } from '../../../types/General';

interface LeadListComponentProps {
    fetchParams: LeadsCallQueryParamsType;
    setFetchParams: (f: LeadsCallQueryParamsType) => void;
    activeTab: LeadTabId;
    setActiveTab: (f: LeadTabId) => void;
}

/**
 * List of leads as table view.
 *
 * @param props LeadListComponentProps
 * @returns React.ReactElement
 */
export default function LeadListComponent(props: LeadListComponentProps): React.ReactElement {
    const { t } = useTranslation();
    const context = useContext(AppContext);
    const workflowStates = useContext(WorkflowStatesContext);

    const [searchParams, setSearchParams] = useSearchParams();
    const [selectedWorkflowFilter, setSelectedWorkflowFilter] = useState<LeadStateType | ''>('');
    const [selectedLeadSourceFilter, setSelectedLeadSourceFilter] = useState<string>('');
    const [leadSpamFilter, setLeadSpamFilter] = useState<boolean>(false);

    const [loaderState, setLoaderState] = useState<LoaderState>({ state: '' });

    const [leadsData, setLeadsData] = useState<LeadsForViewType>({
        page: paginationState,
        list: [],
    });

    function onRefresh() {
        props.setFetchParams({
            ...props.fetchParams,
            page: 0,
        });
    }

    function onFilterLeadspam(doFilter: boolean) {
        if (doFilter) {
            props.setFetchParams({ ...props.fetchParams, leadRateLimitExceeded: false, page: 0 });
        } else {
            const newFetchParams = { ...props.fetchParams, page: 0 };
            delete newFetchParams['leadRateLimitExceeded'];
            props.setFetchParams(newFetchParams);
        }
        setLeadSpamFilter(doFilter);
    }

    function onPageChange(_: React.SyntheticEvent, { activePage }: { [key: string]: any }) {
        const _page = activePage ? +activePage - 1 : 0;

        props.setFetchParams({
            ...props.fetchParams,
            page: _page,
        });
    }

    function onTabChange(tab: LeadTabId) {
        if ((workflowStates.states[tab] || []).length) {
            const fetchParams = props.fetchParams;
            delete fetchParams.leadSource;
            props.setFetchParams({
                ...fetchParams,
                status: workflowStates.states[tab].map((st: string) => st),
                page: 0,
                size: 24,
            });
        }
        setSelectedWorkflowFilter('');
        setSelectedLeadSourceFilter('');
        setSearchParams({ listView: tab });
    }

    function onSort([sortKey, sortValue]: any) {
        const { sort } = props.fetchParams;

        // logik auslagern \\
        let _sort: any = sort ? [...sort] : [];

        if (_sort && _sort.length) {
            const idx = _sort.findIndex((item: any) => item && item[0] === sortKey);

            if (idx > -1) {
                _sort[idx] = [sortKey, sortValue];
            } else {
                _sort.push([sortKey, sortValue]);
            }
        } else {
            _sort.push([sortKey, sortValue]);
        }
        // logik auslagern \\

        props.setFetchParams({
            ...props.fetchParams,
            sort: _sort,
            page: 0,
        });
    }

    function onStateFilter(state: string) {
        let _params = {
            ...props.fetchParams,
            page: 0,
        };
        if (!state) {
            _params.status = workflowStates.states[props.activeTab].map((st: string) => st);
            setSelectedWorkflowFilter('');
        } else {
            _params.status = state;
            setSelectedWorkflowFilter(state as LeadStateType);
        }
        props.setFetchParams(_params);
    }

    function onLeadSourceFilter(leadSource: string) {
        let _params = {
            ...props.fetchParams,
            page: 0,
        };
        if (!leadSource) {
            delete _params.leadSource;
            setSelectedLeadSourceFilter('');
        } else {
            _params.leadSource = leadSource;
            setSelectedLeadSourceFilter(leadSource);
        }
        props.setFetchParams(_params);
    }

    function fetchSucceeded(resp: FetchResponseType) {
        if (resp.status === 401) {
            setLoaderState({ state: 'auth', message: `${t('lead.errors.auth')} ${t('lead.errors.leadData')}.` });
        } else if (resp.status === 200 && resp.data) {
            resp.data.then((data) => {
                setLoaderState({ state: 'loaded' });
                setLeadsData(ACL_importAllLeads(data));
            });
        }
    }

    function getLeadListData(fetchParams: LeadsCallQueryParamsType, token: string) {
        if (token && fetchParams.status?.length) {
            setLoaderState({ state: 'pending' });
            fetchLeads(fetchParams, token).then(fetchSucceeded);
        }
        if (fetchParams.status && !Array.isArray(fetchParams.status)) {
            setSelectedWorkflowFilter(fetchParams.status as LeadStateType);
        } else {
            setSelectedWorkflowFilter('');
        }
        if (fetchParams.leadSource) {
            setSelectedLeadSourceFilter(fetchParams.leadSource);
        } else {
            setSelectedLeadSourceFilter('');
        }
        setLeadSpamFilter(fetchParams.leadRateLimitExceeded == undefined ? false : !fetchParams.leadRateLimitExceeded);
    }

    useEffect(() => {
        getLeadListData(props.fetchParams, context.token);
    }, [props.fetchParams]);

    useEffect(() => {
        props.setActiveTab((searchParams.get('listView') as LeadTabId) || props.activeTab);
    }, [searchParams.get('listView')]);

    useEffect(() => {
        if (workflowStates.error) {
            if (workflowStates.error.status === 401) {
                setLoaderState({ state: 'auth', message: `${t('lead.errors.auth')} ${t('lead.errors.metadata')}.` });
            } else {
                setLoaderState({
                    state: 'error',
                    message: `${t('lead.errors.generic')} ${t('lead.errors.metadata')}.`,
                });
            }
        }
    }, [workflowStates, t]);

    const currentWorkflowStates = (workflowStates.states[props.activeTab]?.sort() as LeadStateType[]) || [];
    const filters = {
        status: (
            <SemanticDropdownWrapperComponent
                options={currentWorkflowStates}
                onChange={onStateFilter}
                selectedOption={selectedWorkflowFilter}
            />
        ),
        leadSource: (
            <SemanticDropdownWrapperComponent
                options={Object.keys(LeadSource)}
                onChange={onLeadSourceFilter}
                selectedOption={selectedLeadSourceFilter}
            />
        ),
    };

    const sorts = {
        leadCreationTimestamp: <LeadsSortingComponent dataKey={'leadCreationTimestamp'} onSort={onSort} />,
    };

    return (
        <>
            <Grid>
                <Grid.Column>
                    <Segment basic>
                        <LeadsListToolbarComponent
                            onRefresh={() => onRefresh()}
                            onFilterLeadSpam={(doFilter: boolean) => onFilterLeadspam(doFilter)}
                            filterLeadSpamActive={leadSpamFilter}
                        />
                    </Segment>
                </Grid.Column>
            </Grid>

            <>
                <Segment basic>
                    <LeadsTabQuickFilterComponent
                        filterParams={props.fetchParams}
                        activeTab={props.activeTab}
                        onTabChange={onTabChange}
                    />
                    <LeadsTableComponent
                        loaderState={loaderState}
                        data={leadsData}
                        tableConfig={config.tableHeader}
                        filters={filters}
                        sorts={sorts}
                    />
                </Segment>
                <Segment basic>
                    <Pagination
                        boundaryRange={0}
                        activePage={leadsData.page.number + 1}
                        ellipsisItem={null}
                        firstItem={null}
                        lastItem={null}
                        siblingRange={1}
                        totalPages={leadsData.page.totalPages}
                        onPageChange={onPageChange}
                    />
                </Segment>
            </>
        </>
    );
}
